import React, { useState } from 'react';
import FileInput, { FileDetails } from '@amzn/meridian/file-input';
import Column from '@amzn/meridian/column';
import { InputAsinsValidationUtils } from '@components/agile/utils/InputAsinsValidationUtils';

export interface AtBulkUploadInputProps {
  setEnableTextArea: (value: boolean) => void;
  setInputAsins: (value: string[]) => void;
  setDisableButton: (value: boolean) => void;
  setInputTableFlag: (value: boolean) => void;
  setRecommendationFlag: (value: boolean) => void;
  setTextAreaInput: (value: string) => void;
}

const AtAcrossZoneBulkUpload = (props: AtBulkUploadInputProps) => {
  const {
    setEnableTextArea,
    setInputAsins,
    setDisableButton,
    setInputTableFlag,
    setRecommendationFlag,
    setTextAreaInput,
  } = props;
  const [file, setFile] = useState<File | null>(null);
  const [percentage, setPercentage] = useState<number>(0);
  const [invalidAsinErrorFlag, setInvalidAsinErrorFlag] = useState<boolean>(false);

  const INVALID_ASIN_IN_FILE = 'Invalid ASIN found in file';

  function parseCsvRow(row: string, asinColumnIndex: number): string {
    const regex = /,(?=(?:[^"']*["'][^"']*["'])*[^"']*$)/g;
    const values = row.split(regex);
    return asinColumnIndex !== -1 ? values[asinColumnIndex].trim() : '';
  }
  async function parseCSVToEntries(inputFile: File): Promise<string[]> {
    const fileReader = new FileReader();
    return new Promise((resolve, reject) => {
      fileReader.onload = (event) => {
        if (event.target && event.target.result) {
          const fileContent = event.target.result as string;
          const rows = fileContent.trim().split('\n');
          const asinList: string[] = [];
          const headerRow = rows[0].split(',');
          const asinColumnIndex = headerRow.findIndex((header) => header.trim().toLowerCase() === 'asin');
          // eslint-disable-next-line no-plusplus
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            const asin = parseCsvRow(row, asinColumnIndex);
            asinList.push(asin);
          }
          resolve(asinList);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      fileReader.onerror = () => {
        reject(fileReader.error);
      };

      fileReader.readAsText(inputFile);
    });
  }
  const handleUpload = (selectedFile1: File) => {
    if (selectedFile1 !== null) {
      parseCSVToEntries(selectedFile1)
        .then((entries: string[]) => {
          if (InputAsinsValidationUtils(entries)) {
            setInputAsins(entries);
            setDisableButton(false);
            setInvalidAsinErrorFlag(false);
          } else {
            setInvalidAsinErrorFlag(true);
            setDisableButton(true);
          }
          return entries;
        })
        .catch((error: Error) => {
          console.error('CSV upload failed while parsing CSV', error);
          throw error;
        });
    }
  };

  const onFileAttached = (acceptedFiles: File[]) => {
    setInputTableFlag(false);
    setRecommendationFlag(false);
    setEnableTextArea(true);
    setTextAreaInput('');
    const newFile = acceptedFiles[0];
    setFile(newFile);
    handleUpload(newFile);
    setPercentage(100);
  };

  const onClickCancel = () => {
    setFile(null);
    setEnableTextArea(false);
    setDisableButton(true);
    setInputTableFlag(false);
    setRecommendationFlag(false);
  };

  const onClickRemoveFile = () => {
    setFile(null);
    setEnableTextArea(false);
    setDisableButton(true);
    setInputTableFlag(false);
    setRecommendationFlag(false);
  };

  return (
    <Column spacing="200" spacingInset="300">
      <FileInput type="single" onFileAttached={onFileAttached} accept="text/csv" data-testid="bulk-input-file-input" />
      {file && (
        <FileDetails
          file={file}
          onClickCancel={onClickCancel}
          onClickRemoveFile={onClickRemoveFile}
          cancelUploadButtonLabel={`Cancel, ${file.name}`}
          removeFileButtonLabel={`Remove, ${file.name}`}
          uploadPercentage={percentage}
          uploadComplete={percentage === 100}
          error={invalidAsinErrorFlag}
          errorMessage={INVALID_ASIN_IN_FILE}
        />
      )}
    </Column>
  );
};

export default AtAcrossZoneBulkUpload;
